import { memo, useState } from 'react';
import { NodeResizer, NodeProps, useStore, useReactFlow } from 'reactflow';
import { brandColors } from '@duke-hq/ui/theme/chakraTheme';
import { Box, IconButton, Menu, MenuButton, MenuItem, MenuList, Tag } from '@chakra-ui/react';
import { getRelativeNodesBounds } from './utils';
import { MoreVertical, Trash2 } from 'lucide-react';

function DefaultContainer({ id, selected, data }: NodeProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { setNodes, deleteElements } = useReactFlow();

  const { minWidth, minHeight } = useStore((store) => {
    const childNodes = Array.from(store.nodeInternals.values()).filter((n) => n.parentNode === id);

    const rect = getRelativeNodesBounds(childNodes);

    return {
      minWidth: rect.x + rect.width,
      minHeight: rect.y + rect.height,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  return (
    <>
      <NodeResizer
        color={brandColors.purple[600]}
        handleStyle={{ width: '8px', height: '8px' }}
        minHeight={minHeight}
        minWidth={minWidth}
        isVisible={selected}
      />
      <Box
        zIndex={1010}
        onClick={(ev) => {
          ev.stopPropagation();
        }}
      >
        <Menu
          onOpen={() => {
            setNodes((nodes) =>
              nodes.map((node) => {
                if (node.id == id) {
                  node.selected = true;
                } else {
                  node.selected = false;
                }
                return node;
              }),
            );
            setIsMenuOpen(true);
          }}
          onClose={() => setIsMenuOpen(false)}
        >
          <MenuButton
            as={IconButton}
            top="5px"
            right="5px"
            pos="absolute"
            aria-label="Node actions"
            variant="ghost"
            size="xs"
          >
            <MoreVertical size="18" color="currentColor" cursor="pointer" />
          </MenuButton>
          {isMenuOpen ? (
            <MenuList w={'170px'}>
              <MenuItem
                icon={<Trash2 />}
                onClick={() => {
                  deleteElements({ nodes: [{ id }] });
                }}
              >
                Delete
              </MenuItem>
            </MenuList>
          ) : null}
        </Menu>
      </Box>

      <Tag
        backgroundColor={brandColors.gray[900]}
        color={brandColors.gray[50]}
        textTransform="uppercase"
        fontWeight="bold"
        fontSize="xs"
        m={2}
      >
        {data.label}
      </Tag>
    </>
  );
}

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export const MemoizedDefaultContainer = memo(DefaultContainer);
